import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { ConsultationDialog } from "./ConsultationDialog";
import { SERVICES } from "../lib/services";
import {
  ABOUT_LINKS,
  WEALTH_LINKS,
  INVESTMENT_SOLUTIONS,
  CLIENT_SEGMENTS,
  INSIGHTS_LINKS,
  CONTACT_LINKS,
} from "../lib/menus";

const navLinks = [
  { to: "/about", label: "About", dropdown: "about" },
  { to: "/wealth-management", label: "Wealth Management", dropdown: "wealth" },
  { to: "/services", label: "Investment Solutions", dropdown: "investments" },
  { to: "/client-segments", label: "Client Segments", dropdown: "segments" },
  { to: "/insights", label: "Insights", dropdown: "insights" },
  { to: "/contact", label: "Contact", dropdown: "contact" },
];

function SimpleDropdown({ items, prefix, testIdPrefix, columns = 1, basePath }) {
  const cols = columns === 2 ? "grid-cols-2" : "grid-cols-1";
  const width = columns === 2 ? "w-[460px]" : "w-[280px]";
  return (
    <div className="absolute top-full left-1/2 -translate-x-1/2 pt-5">
      <div className={`bg-[#FAF5EB] border border-[#2A0A0E]/10 shadow-2xl shadow-[#2A0A0E]/20 ${width} grid ${cols} gap-0 p-3`}>
        {items.map((it) => (
          <Link
            key={it.slug}
            to={`${basePath}/${it.slug}`}
            data-testid={`${testIdPrefix}-${it.slug}`}
            className="block px-4 py-3 group hover:bg-[#7A1F26]/5 transition-colors"
          >
            <p className="text-sm text-[#2A0A0E] font-medium group-hover:text-[#7A1F26]">{it.label}</p>
            {it.desc && <p className="text-xs text-[#2A0A0E]/60 mt-0.5">{it.desc}</p>}
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 bg-[#FBF6EC] ${
        scrolled ? "shadow-sm border-b border-[#2A0A0E]/10" : "border-b border-[#2A0A0E]/5"
      }`}
    >
      <div className="container-myc flex items-center justify-between h-24">
        <Link to="/" data-testid="brand-logo-link" className="flex items-center group">
          <img
            src="/moneyyard-mark.png"
            alt="Moneyyard Capital"
            data-testid="brand-logo-image"
            className="h-12 md:h-[54px] w-auto object-contain shrink-0"
          />
          <div className="ml-2 md:ml-3 flex flex-col items-stretch leading-none">
            <div className="flex items-baseline">
              <span className="font-display text-[22px] md:text-[28px] tracking-[-0.015em] font-black text-[#7A1F26]">MONEY</span>
              <span className="font-display text-[22px] md:text-[28px] tracking-[-0.015em] font-black text-[#C9A961]">YARD</span>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <span className="flex-1 h-[1.2px] bg-gradient-to-r from-transparent via-[#C9A961] to-[#C9A961]" />
              <span className="font-display text-[9px] md:text-[11px] tracking-[0.55em] uppercase text-[#2A0A0E] font-semibold">Capital</span>
              <span className="flex-1 h-[1.2px] bg-gradient-to-l from-transparent via-[#C9A961] to-[#C9A961]" />
            </div>
            <span className="hidden md:block font-display italic text-[10px] text-[#7A1F26] mt-0.5 tracking-wide text-center">
              Architects of Capital
            </span>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-5 xl:gap-7">
          {navLinks.map((l) =>
            l.dropdown ? (
              <div
                key={l.label}
                className="relative"
                onMouseEnter={() => setOpenDropdown(l.dropdown)}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <NavLink
                  to={l.to}
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={({ isActive }) =>
                    `inline-flex items-center gap-1 text-[11px] xl:text-[12px] tracking-[0.15em] uppercase font-medium transition-colors ${
                      isActive ? "text-[#7A1F26]" : "text-[#2A0A0E]/70 hover:text-[#7A1F26]"
                    }`
                  }
                >
                  {l.label}
                  <ChevronDown className="w-3 h-3" />
                </NavLink>

                {openDropdown === "about" && l.dropdown === "about" && (
                  <SimpleDropdown items={ABOUT_LINKS} testIdPrefix="nav-about" basePath="/about" />
                )}

                {openDropdown === "wealth" && l.dropdown === "wealth" && (
                  <SimpleDropdown
                    items={WEALTH_LINKS}
                    testIdPrefix="nav-wealth"
                    basePath="/wealth-management"
                    columns={2}
                  />
                )}

                {openDropdown === "investments" && l.dropdown === "investments" && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 pt-5">
                    <div className="bg-[#FAF5EB] border border-[#2A0A0E]/10 shadow-2xl shadow-[#2A0A0E]/20 w-[640px] grid grid-cols-2 gap-1 p-3">
                      {INVESTMENT_SOLUTIONS.map((s) => (
                        <Link
                          key={s.slug}
                          to={`/services/${s.slug}`}
                          data-testid={`nav-investment-${s.slug}`}
                          className="px-4 py-3 text-sm text-[#2A0A0E]/80 hover:text-[#7A1F26] hover:bg-[#7A1F26]/5 transition-colors"
                        >
                          {s.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {openDropdown === "segments" && l.dropdown === "segments" && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 pt-5">
                    <div className="bg-[#FAF5EB] border border-[#2A0A0E]/10 shadow-2xl shadow-[#2A0A0E]/20 w-[860px] grid grid-cols-2 gap-1 p-4">
                      {CLIENT_SEGMENTS.map((seg) => (
                        <Link
                          key={seg.slug}
                          to={`/client-segments/${seg.slug}`}
                          data-testid={`nav-segment-${seg.slug}`}
                          className="block p-4 group hover:bg-[#7A1F26]/5 transition-colors border-l border-[#7A1F26]/15"
                        >
                          <p className="font-display text-base text-[#2A0A0E] font-semibold group-hover:text-[#7A1F26]">
                            {seg.title}
                          </p>
                          <p className="text-[11px] tracking-wider uppercase text-[#C9A961] mt-1 font-medium">
                            {seg.asset}
                          </p>
                          <p className="text-[12px] text-[#2A0A0E]/65 mt-2 leading-relaxed">{seg.desc}</p>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {openDropdown === "services" && l.dropdown === "services" && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 pt-5">
                    <div className="bg-[#FAF5EB] border border-[#2A0A0E]/10 shadow-2xl shadow-[#2A0A0E]/20 w-[640px] grid grid-cols-2 gap-1 p-3">
                      {SERVICES.map((s) => (
                        <Link
                          key={s.slug}
                          to={`/services/${s.slug}`}
                          data-testid={`nav-service-${s.slug}`}
                          className="px-4 py-3 text-sm text-[#2A0A0E]/80 hover:text-[#7A1F26] hover:bg-[#7A1F26]/5 transition-colors"
                        >
                          {s.title}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {openDropdown === "insights" && l.dropdown === "insights" && (
                  <SimpleDropdown items={INSIGHTS_LINKS} testIdPrefix="nav-insights" basePath="/insights" />
                )}

                {openDropdown === "contact" && l.dropdown === "contact" && (
                  <SimpleDropdown items={CONTACT_LINKS} testIdPrefix="nav-contact" basePath="/contact" />
                )}
              </div>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `text-[11px] xl:text-[12px] tracking-[0.15em] uppercase font-medium transition-colors ${
                    isActive ? "text-[#7A1F26]" : "text-[#2A0A0E]/70 hover:text-[#7A1F26]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden lg:block">
          <ConsultationDialog
            trigger={
              <button
                data-testid="navbar-book-consultation-button"
                className="btn-gold !px-6 !py-3"
              >
                Book Consultation
              </button>
            }
          />
        </div>

        {/* Mobile toggle */}
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((s) => !s)}
          className="lg:hidden p-2 text-[#2A0A0E]"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-[#FBF6EC] border-t border-[#2A0A0E]/10"
        >
          <div className="container-myc py-6 flex flex-col gap-1">
            {navLinks.map((l) => (
              <NavLink
                key={l.label}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `py-3 text-base ${
                    isActive ? "text-[#7A1F26]" : "text-[#2A0A0E]/80"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="pt-4">
              <ConsultationDialog
                trigger={
                  <button
                    data-testid="mobile-book-consultation-button"
                    className="btn-gold w-full"
                  >
                    Book Consultation
                  </button>
                }
              />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
